import { useState, useEffect, useCallback, useRef } from "react";
import Loading from "../../Common/Loading"

interface SaleInit {
  family: string;
  suburb: string;
  email: string;
  address: string;
  postcode: string;
  country: string;
  gst: number;
  pst: number;
  hst: number;
  currency: string;
  show_hardlocks: boolean;
  hardlocks_price_per_unit: number;
  login_id: string;
  type_of_sale: string;
  salesman_login: string;
  login_trainer: string;
  login_telemarketer: string;
  login_lead_collector: string;
}

interface Package {
  PackageCode: number;
  PackageDescription: string;
  FamilyPrice: number;
  NoOfMDs: number;
  Subjects: string;
  ProdIDs: string;
}

interface CustomPayment {
  id: number;
  amount: string;
  date: string;
  mode: string; // "0"=Cash "1"=Cheque "2"=CreditCard "3"=DirectDebit
}

interface SelectedPackage {
  package_code: number;
  name: string;
  price: number;
  prod_ids: string[];
  subjects: string[];
  expiry_date: string;
}

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

const HARDLOCKS_UNIT_PRICE = 75;

document.body.style.backgroundColor = "azure";
document.body.style.fontFamily = "Times New Roman, serif";

function Toast({ msg, type, onClose }: { msg: string; type: string; onClose: () => void }) {
  useEffect(() => { const t = setTimeout(onClose, 4500); return () => clearTimeout(t); }, [onClose]);
  const bg = type === "error" ? "#dc2626" : type === "success" ? "#16a34a" : "#1d4ed8";
  return (
    <div onClick={onClose} style={{
      position: "fixed", bottom: 28, right: 28, background: bg, color: "#fff",
      padding: "13px 20px", borderRadius: 4, fontFamily: "Tahoma, Arial, sans-serif",
      fontSize: 15, fontWeight: 400, boxShadow: "2px 2px 6px rgba(0,0,0,.3)",
      zIndex: 9999, cursor: "pointer", maxWidth: 400, lineHeight: 1.55,
    }}>{msg}</div>
  );
}


function PackageModal({
  packages, selected, onSelect, onClose,
}: {
  packages: Package[];
  selected: SelectedPackage[];
  onSelect: (pkgs: SelectedPackage[]) => void;
  onClose: () => void;
}) {
  const [checked, setChecked] = useState<Set<number>>(
    new Set(selected.map((s) => s.package_code))
  );

  document.body.style.background = "azure";
  const toggle = (code: number) =>
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });

  const confirm = () => {
    const result: SelectedPackage[] = packages
      .filter((p) => checked.has(p.PackageCode))
      .map((p) => ({
        package_code: p.PackageCode,
        name: p.PackageDescription,
        price: p.FamilyPrice,
        prod_ids: p.ProdIDs ? p.ProdIDs.split(",").map((x) => x.trim()) : [],
        subjects: p.Subjects ? p.Subjects.split(",").map((x) => x.trim()) : [],
        expiry_date: "01/01/2030",
      }));
    onSelect(result);
    onClose();
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,.4)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 8000,
    }}>
      <div style={{
        background: "#f0f0f0", border: "2px solid #7f9db9",
        borderRadius: 0, width: 520, maxHeight: "80vh",
        display: "flex", flexDirection: "column",
        boxShadow: "3px 3px 8px rgba(0,0,0,.3)",
        // fontFamily: "Tahoma, Arial, sans-serif",
      }}>
        <div style={{
          background: "#aad5ff",
          borderBottom: "2px solid #AAD5FF",
          color: "#000", padding: "6px 10px",
          fontWeight: 700, fontSize: 15,
        }}>
          Select Package(s)
        </div>
        <div style={{ overflowY: "auto", flex: 1 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 15 }}>
            <thead>
              <tr style={{ background: "#b8cfe8" }}>
                <th style={th}></th>
                <th style={{ ...th, textAlign: "left" }}>Package Name</th>
                <th style={{ ...th, textAlign: "right" }}>Price</th>
              </tr>
            </thead>
            <tbody>
              {packages.map((p) => (
                <tr key={p.PackageCode}
                  onClick={() => toggle(p.PackageCode)}
                  style={{
                    cursor: "pointer",
                    background: checked.has(p.PackageCode) ? "#cce4f7" : "#fff",
                    borderBottom: "2px solid #AAD5FF",
                  }}
                >
                  <td style={{ ...td, width: 30, textAlign: "center" }}>
                    <input type="checkbox" readOnly checked={checked.has(p.PackageCode)}
                      style={{ cursor: "pointer" }} />
                  </td>
                  <td style={td}>{p.PackageDescription}</td>
                  <td style={{ ...td, textAlign: "right"}}>
                    {p.FamilyPrice?.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{
          padding: "8px 10px", borderTop: "2px solid #AAD5FF",
          display: "flex", gap: 6, justifyContent: "flex-end",
          background: "#f0f0f0",
        }}>
          <button onClick={onClose} style={{ ...btnSecondary }}>Cancel</button>
          <button onClick={confirm} style={{ ...btnPrimary }}>Confirm Selection</button>
        </div>
      </div>
    </div>
  );
}


function CustomPaymentModal({
  onAdd, onClose,
}: {
  onAdd: (cp: Omit<CustomPayment, "id">) => void;
  onClose: () => void;
}) {
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState("");
  const [mode, setMode] = useState("0");

  const submit = () => {
    if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
      alert("Enter a valid amount");
      return;
    }
    onAdd({ amount, date, mode });
    onClose();
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,.4)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 8100,
    }}>
      <div style={{
        background: "#f0f0f0", border: "2px solid #7f9db9",
        borderRadius: 0, width: 340,
        boxShadow: "3px 3px 8px rgba(0,0,0,.3)",
        // fontFamily: "Tahoma, Arial, sans-serif",
      }}>
        <div style={{
          background: "#aad5ff",
          borderBottom: "2px solid #AAD5FF",
          color: "#000", padding: "6px 10px",
          fontWeight: 700, fontSize: 15,
        }}>Custom Payment</div>
        <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
          <label style={labelStyle}>
            Amount <span style={{ color: "#dc2626" }}>*</span>
            <input style={inputStyle} type="number" step="0.01" value={amount}
              onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
          </label>
          <label style={labelStyle}>
            Payment Date (dd/MM/yyyy)
            <input style={inputStyle} type="text" value={date}
              onChange={(e) => setDate(e.target.value)} placeholder="dd/MM/yyyy" maxLength={10} />
          </label>
          <label style={labelStyle}>
            Payment Mode
            <select style={inputStyle} value={mode} onChange={(e) => setMode(e.target.value)}>
              <option value="0">Cash</option>
              <option value="1">Cheque</option>
              <option value="2">Credit Card</option>
              <option value="3">Direct Debit</option>
            </select>
          </label>
        </div>
        <div style={{
          padding: "8px 16px", borderTop: "2px solid #AAD5FF",
          display: "flex", gap: 6, justifyContent: "flex-end",
          background: "#f0f0f0",
        }}>
          <button onClick={onClose} style={btnSecondary}>Cancel</button>
          <button onClick={submit} style={btnPrimary}>Add Payment</button>
        </div>
      </div>
    </div>
  );
}


export default function AddPackage() {
  const [init, setInit] = useState<SaleInit | null>(null);
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: string } | null>(null);

  const [showPackageModal, setShowPackageModal] = useState(false);
  const [selectedPackages, setSelectedPackages] = useState<SelectedPackage[]>([]);

  const [hardlocksCount, setHardlocksCount] = useState(0);

  const [discountExcTax, setDiscountExcTax] = useState("0.00");
  const [depositIncTax, setDepositIncTax] = useState("0.00");
  const [deposit, setDeposit] = useState("0.00");
  const [discount, setDiscount] = useState("0.00");

  const [subTotal, setSubTotal] = useState(0);
  const [gstAmt, setGstAmt] = useState(0);
  const [pstAmt, setPstAmt] = useState(0);
  const [hstAmt, setHstAmt] = useState(0);
  const [totalIncTax, setTotalIncTax] = useState(0);
  const [balance, setBalance] = useState(0);

  const [monitoringEnabled, setMonitoringEnabled] = useState(false);
  const [monitoringMode, setMonitoringMode] = useState<"mail" | "online" | null>(null);
  const [mailAddress, setMailAddress] = useState("");
  const [email1, setEmail1] = useState("");
  const [email2, setEmail2] = useState("");
  const [email3, setEmail3] = useState("");

  const [supportFee, setSupportFee] = useState("195.00");
  const [freeSupport, setFreeSupport] = useState("12");

  const [depositMethod, setDepositMethod] = useState<"Cash" | "Cheque" | "Credit Card" | "Direct Debit">("Cash");
  const [officeReceived, setOfficeReceived] = useState(false);
  const [depositBSB, setDepositBSB] = useState("");
  const [depositAc, setDepositAc] = useState("");
  const [depositAcName, setDepositAcName] = useState("");
  const [depositBankName, setDepositBankName] = useState("");
  const [collectionDate, setCollectionDate] = useState("");

  const [paymentContactSame, setPaymentContactSame] = useState(true);
  const [payeeContactDetails, setPayeeContactDetails] = useState("");

  const [installFreq, setInstallFreq] = useState(0);
  const [installStartDate, setInstallStartDate] = useState("");
  const [installAmount, setInstallAmount] = useState("");
  const [installCount, setInstallCount] = useState("");
  const [paymentMode, setPaymentMode] = useState(0);

  const [balanceBSB, setBalanceBSB] = useState("");
  const [balanceAcNo, setBalanceAcNo] = useState("");
  const [balanceAcName, setBalanceAcName] = useState("");
  const [balanceBankName, setBalanceBankName] = useState("");

  const [customPayments, setCustomPayments] = useState<CustomPayment[]>([]);
  const [showCPModal, setShowCPModal] = useState(false);
  const [cpIdCounter, setCpIdCounter] = useState(0);
  const [creditCardType, setCreditCardType] = useState("0");
  const [financeOption, setFinanceOption] = useState(false);
  const [remainingBalance, setRemainingBalance] = useState(0);

  const [orderComments, setOrderComments] = useState("");

  document.body.style.backgroundColor = "azure";

  const showToast = (msg: string, type = "info") => setToast({ msg, type });

  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE_URL}api/sales/init/`).then((r) => r.json()),
      fetch(`${API_BASE_URL}api/sales/packages/`).then((r) => r.json()),
    ])
      .then(([initData, pkgData]) => {
        setInit(initData);
        setPackages(pkgData.packages || []);
        if (initData.email) setEmail1(initData.email);
        if (initData.address && initData.suburb && initData.postcode) {
          setMailAddress(`${initData.address}\n${initData.suburb} - ${initData.postcode}`);
        }
      })
      .catch(() => showToast("Failed to load page data.", "error"));
  }, []);

  const recalculate = useCallback(() => {
    if (!init) return;
    const { gst, pst, hst, country } = init;
    const isCanada = country === "CANADA";

    const pkgAmt = selectedPackages.reduce((s, p) => s + p.price, 0);
    const hlAmt  = hardlocksCount * HARDLOCKS_UNIT_PRICE;
    const discExc = parseFloat(discountExcTax) || 0;
    const dep     = isCanada ? 0 : (parseFloat(deposit) || 0);
    const discVal = isCanada ? 0 : (parseFloat(discount) || 0);
    const depIT   = parseFloat(depositIncTax) || 0;

    let sub = pkgAmt + hlAmt - discExc - dep;
    sub = Math.max(sub, 0);

    const g = parseFloat(((gst / 100) * sub).toFixed(2));
    const p = parseFloat(((pst / 100) * sub).toFixed(2));
    const h = parseFloat(((hst / 100) * sub).toFixed(2));
    const totalIT = parseFloat((sub + g + p + h).toFixed(2));

    let bal = 0;
    if (pkgAmt > 0 && pkgAmt >= discExc) {
      bal = parseFloat((totalIT - depIT).toFixed(2));
      if (bal < 0) bal = 0;
    }

    setSubTotal(parseFloat(sub.toFixed(2)));
    setGstAmt(g);
    setPstAmt(p);
    setHstAmt(h);
    setTotalIncTax(totalIT);
    setBalance(bal);
  }, [init, selectedPackages, hardlocksCount, discountExcTax, deposit, discount, depositIncTax]);

  useEffect(() => { recalculate(); }, [recalculate]);

  useEffect(() => {
    const customTotal = customPayments.reduce((s, cp) => s + (parseFloat(cp.amount) || 0), 0);
    let rem: number;
    if (installFreq > 0 && installAmount && installCount) {
      const ia = parseFloat(installAmount) || 0;
      const ic = parseFloat(installCount) || 0;
      rem = parseFloat((balance - ia * ic - customTotal).toFixed(2));
    } else {
      rem = parseFloat((balance - customTotal).toFixed(2));
    }
    setRemainingBalance(rem);
  }, [balance, installFreq, installAmount, installCount, customPayments]);

  const showDepositSection = parseFloat(depositIncTax) > 0;
  const showDD_balance = paymentMode === 4 || customPayments.some((cp) => cp.mode === "3");

  const handleMonitoringToggle = (checked: boolean) => {
    setMonitoringEnabled(checked);
    if (!checked) {
      setMonitoringMode(null);
    } else if (init && !init.show_hardlocks) {
      setMonitoringMode("online");
      setEmail1(init.email || "");
    }
  };

  const validateEmail = (v: string) =>
    !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

  const onlyDecimal = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const k = e.key;
    if (k === "." && (e.currentTarget.value.includes("."))) { e.preventDefault(); return; }
    if (!/[\d.]/.test(k) && !["Backspace","ArrowLeft","ArrowRight","Tab"].includes(k)) e.preventDefault();
  };

  const onlyNumbers = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!/[\d]/.test(e.key) && !["Backspace","ArrowLeft","ArrowRight","Tab"].includes(e.key))
      e.preventDefault();
  };

  const validate = (): boolean => {
    if (!init) return false;
    const { country } = init;
    const isCanada = country === "CANADA";

    if (selectedPackages.length === 0) {
      alert("Please select a Package and complete the form");
      return false;
    }
    if (!isCanada) {
      const dep = parseFloat(deposit);
      if (isNaN(dep) || dep < 0) { alert("Please enter Deposit Amount"); return false; }
      const disc = parseFloat(discount);
      if (isNaN(disc) || disc < 0) { alert("Please enter Discount Amount"); return false; }
    }
    if (monitoringEnabled) {
      if (!monitoringMode) { alert("Please choose an Option for Monitoring"); return false; }
      if (monitoringMode === "online" && !email1 && !email2 && !email3) {
        alert("Please provide at least ONE Email ID"); return false;
      }
      if (!validateEmail(email1) || !validateEmail(email2) || !validateEmail(email3)) {
        alert("Invalid Email ID"); return false;
      }
    }
    const sf = parseFloat(supportFee);
    if (isNaN(sf) || sf < 0) { alert("Please enter Support Fee"); return false; }
    const fs = parseInt(freeSupport);
    if (isNaN(fs) || fs < 1) { alert("Please input the number of months for Free Support"); return false; }

    const dep = isCanada ? parseFloat(depositIncTax) : parseFloat(deposit);
    if (dep > 0 && !depositMethod) { alert("Please choose a method for Deposit"); return false; }
    if (depositMethod === "Direct Debit") {
      if (!depositBSB) { alert("Enter BSB"); return false; }
      if (!depositAc) { alert("Enter Account Number"); return false; }
      if (!depositAcName) { alert("Enter Account Name"); return false; }
      if (!depositBankName) { alert("Enter Bank Name"); return false; }
    }
    if (balance <= 0) { alert("Balance cannot be ZERO"); return false; }

    let bpoFlag = 0;
    if (installFreq < 1 && !installAmount && !installCount && paymentMode < 1) bpoFlag++;
    if (customPayments.length === 0) bpoFlag++;
    if (bpoFlag > 1 && !financeOption) { alert("Please choose at least one Balance Payment Option"); return false; }

    if (installFreq > 0) {
      if (!installAmount) { alert("Please type Installment Amount"); return false; }
      if (!installCount) { alert("Please type No. of Installments"); return false; }
      if (paymentMode < 1) { alert("Please select a Payment Mode"); return false; }
      if (paymentMode === 4 && (!balanceBSB || !balanceAcNo || !balanceAcName || !balanceBankName)) {
        alert("Please provide details for Direct Debit"); return false;
      }
    }

    if (financeOption && remainingBalance > 0) {
      if (!window.confirm(`Balance amount of ${init.currency} ${remainingBalance.toFixed(2)} is chosen for Finance`))
        return false;
    } else if (!financeOption) {
      const customTotal = customPayments.reduce((s, cp) => s + (parseFloat(cp.amount) || 0), 0);
      const regularTotal = installFreq > 0 ? (parseFloat(installAmount)||0) * (parseInt(installCount)||0) : 0;
      const bpoTotal = regularTotal + customTotal;
      if (customPayments.length > 0 && Math.abs(bpoTotal - balance) > 0.01) {
        alert("Your payments do not match the balance amount"); return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    if (!window.confirm("This will confirm the sale and exit to menu.")) return;

    setLoading(true);
    try {
      const isCanada = init?.country === "CANADA";
      const body = {
        country: init?.country || "",
        selected_packages: selectedPackages,
        deposit_amount: parseFloat(deposit) || 0,
        deposit_amount_inc_tax: parseFloat(depositIncTax) || 0,
        discount_amount: parseFloat(discount) || 0,
        discount_amount_exc_tax: parseFloat(discountExcTax) || 0,
        total_amount: subTotal,
        total_inc_tax: totalIncTax,
        balance_amount: balance,
        support_fee: parseFloat(supportFee) || 0,
        free_support_months: parseInt(freeSupport) || 12,
        hardlocks_count: hardlocksCount,
        hardlocks_price: hardlocksCount * HARDLOCKS_UNIT_PRICE,
        gst: init?.gst || 0,
        pst: init?.pst || 0,
        hst: init?.hst || 0,
        is_finance: financeOption,
        remaining_balance: remainingBalance,
        deposit_method: depositMethod,
        deposit_bsb: depositBSB,
        deposit_ac: depositAc,
        deposit_ac_name: depositAcName,
        deposit_bank: depositBankName,
        collection_date: collectionDate,
        office_received: officeReceived,
        mail_reporting: monitoringMode === "mail",
        online_reporting: monitoringMode === "online",
        mail_address: mailAddress,
        email1, email2, email3,
        installment_frequency: installFreq,
        installment_amount: parseFloat(installAmount) || 0,
        no_of_installments: parseInt(installCount) || 0,
        bpo_payment_mode: ["", "Cash", "Cheque", "Credit Card", "Direct Debit"][paymentMode] || "",
        balance_bsb: balanceBSB,
        balance_ac_no: balanceAcNo,
        balance_ac_name: balanceAcName,
        balance_bank: balanceBankName,
        show_dd: showDD_balance,
        custom_payments: customPayments.map((cp) => ({
          amount: parseFloat(cp.amount),
          date: cp.date,
          mode: cp.mode,
        })),
        payee_contact_details: payeeContactDetails,
        order_comments: orderComments,
        credit_card_type: creditCardType,
        display_cc: paymentMode === 3,
      };

      const res = await fetch(`${API_BASE_URL}api/sales/submit/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        showToast("Sale submitted successfully!", "success");
        setTimeout(() => {
          window.location.href = data.redirect === "finance_sales"
            ? "/finance/sales/"
            : "/sales/modify/";
        }, 1200);
      } else {
        showToast(data.error || "Submission failed.", "error");
      }
    } catch {
      showToast("Network error. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const addCustomPayment = (cp: Omit<CustomPayment, "id">) => {
    if (balance <= 0) { alert("Balance is zero."); return; }
    const id = cpIdCounter + 1;
    setCpIdCounter(id);
    setCustomPayments((prev) => [...prev, { ...cp, id }]);
  };

  const removeCustomPayment = (id: number) =>
    setCustomPayments((prev) => prev.filter((c) => c.id !== id));

  const modeLabel = (m: string) =>
    ({ "0": "Cash", "1": "Cheque", "2": "Credit Card", "3": "Direct Debit" }[m] || m);

  const packageTotal = selectedPackages.reduce((s, p) => s + p.price, 0);
  const isCanada = init?.country === "CANADA";
  const currency = init?.currency || "A$";

  if (!init) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", color:"#444", fontSize: 15 }}>
      <Loading />
    </div>
  );

  return (
    <>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #d4e8f0; font-family: Tahoma, Arial, sans-serif; font-size: 15px; }
        input[type=text], input[type=email], input[type=number], select, textarea {
          font-family: Tahoma, Arial, sans-serif;
          font-size: 15px;
        }
        input:focus, select:focus, textarea:focus {
          outline: 1px solid #6699cc;
        }
        ::-webkit-scrollbar { width: 14px; }
        ::-webkit-scrollbar-track { background: #f0f0f0; border: 1px solid #ccc; }
        ::-webkit-scrollbar-thumb { background: #b0b8c8; border: 1px solid #8899aa; }
      `}</style>

      <div style={{
        maxWidth: 920, margin: "8px auto", padding: "0 8px 20px", fontSize: 15,
      }}>

        {/* ── Family / Suburb bar ── */}
        <table style={{
          width: "100%", borderCollapse: "collapse",
          border: "2px solid #AAD5FF", marginBottom: 10,
        }}>
          <tbody>
            <tr style={{ background: "azure" }}>
              <td style={{ width: "50%", padding: "5px 10px", borderRight: "1px solid #7f9db9" }}>
                <span style={{ color: "#1d1d1d", fontSize: 16 }}>Family : </span>
                <span style={{ fontWeight: 700, fontSize: 16 }}>{init.family || ""}</span>
              </td>
              <td style={{ width: "50%", padding: "5px 10px" }}>
                <span style={{ color: "#1d1d1d", fontSize: 16 }}>Suburb : </span>
                <span style={{ fontWeight: 700, fontSize: 16 }}>{init.suburb || ""}</span>
              </td>
            </tr>
          </tbody>
        </table>

        {/* ── Main pricing table ── */}
        <table style={{
          width: "100%", borderCollapse: "collapse",
          border: "2px solid #AAD5FF", borderTop: "none",
          background: "#fff",
        }}>
          <thead>
            <tr style={{ background: "#aad5ff" }}>
              <td style={{
                padding: "6px 10px", fontWeight: 400, fontSize: 16,
                width: "65%",
              }}>
                Package
              </td>
              <td style={{
                padding: "6px 10px", textAlign: "right", fontSize: 16,
              }}>
                <div style={{ fontWeight: 400 }}>Family</div>
                <div style={{ fontWeight: 400 }}>R.R. Price {currency}</div>
              </td>
            </tr>
          </thead>
          <tbody style={{background:'azure'}}>
            {/* Select package row */}
            <tr style={{ borderBottom: "2px solid #AAD5FF" }}>
              <td style={{ padding: "10px 10px", borderRight: "2px solid #AAD5FF" }}>
                <button onClick={() => window.location.href="/SALES/NewSales/EL_SelectPackages"} style={{
                  background: "none", border: "none",
                  color: "#0000cc",
                  fontSize: 18, cursor: "pointer", textDecoration: "underline", padding: 0, fontFamily: "Times New Roman, serif",
                }}>
                  Select Package(s)
                </button>
                {selectedPackages.length > 0 && (
                  <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 4, fontSize: 15 }}>
                    <thead>
                      <tr style={{ background: "#dbeafe" }}>
                        <th style={{ ...th, textAlign: "left", fontSize: 15 }}>Package Name</th>
                        <th style={{ ...th, textAlign: "right", fontSize: 15 }}>Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPackages.map((p) => (
                        <tr key={p.package_code} style={{ borderBottom: "1px solid #ccd9e8" }}>
                          <td style={{ ...td, fontSize: 15 }}>{p.name}</td>
                          <td style={{ ...td, textAlign: "right", fontSize: 15 }}>
                            {p.price.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </td>
              <td style={{ padding: "6px 10px", textAlign: "right" }}>
                <span style={{ fontSize: 15 }}>{packageTotal.toFixed(2)}</span>
              </td>
            </tr>

            {/* Hardlocks rows */}
            {init.show_hardlocks && (
              <>
                <tr style={{ borderBottom: "1px solid #ccd9e8" }}>
                  <td style={{ padding: "4px 10px", borderRight: "2px solid #AAD5FF", color: "#444" }}>
                    Extra Hardlocks
                  </td>
                  <td style={{ padding: "4px 10px", textAlign: "right", color: "#444" }}>
                    Price {currency} {HARDLOCKS_UNIT_PRICE}
                  </td>
                </tr>
                <tr style={{ borderBottom: "1px solid #ccd9e8" }}>
                  <td style={{ padding: "4px 10px", borderRight: "2px solid #AAD5FF" }}>
                    <select
                      value={hardlocksCount}
                      onChange={(e) => setHardlocksCount(parseInt(e.target.value))}
                      style={{ ...inputStyle, width: 100 }}
                    >
                      <option value={0}>Select</option>
                      {[1,2,3,4,5,6].map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: "4px 10px", textAlign: "right" }}>
                    {(hardlocksCount * HARDLOCKS_UNIT_PRICE).toFixed(2)}
                  </td>
                </tr>
              </>
            )}

            {/* GST/PST/HST info */}
            <tr style={{ background: "azure", border: "2px solid #AAD5FF" }}>
              <td colSpan={2} style={{ padding: "8px 10px", color: "#444", fontSize:16}}>
                GST : {init.gst} %,&nbsp; PST : {init.pst} %,&nbsp; HST : {init.hst} %
              </td>
            </tr>

            {/* Discount exc taxes (Canada only) */}
            {isCanada && (
              <tr style={{ borderBottom: "1px solid #ccd9e8" }}>
                <td style={{ padding: "4px 10px", textAlign: "right", borderRight: "2px solid #AAD5FF", color: "#444" }}>
                  Discount (exc taxes)
                </td>
                <td style={{ padding: "4px 10px", textAlign: "right" }}>
                  <input style={{ ...inputStyle, width: 150, textAlign: "right" }}
                    value={discountExcTax} onKeyDown={onlyDecimal}
                    onChange={(e) => setDiscountExcTax(e.target.value)}
                    onBlur={recalculate} maxLength={12} />
                </td>
              </tr>
            )}

            {/* Sub Total */}
            <tr style={{ fontSize:15, backgroundColor:"azure"}}>
              <td style={{ padding: "4px 10px", textAlign: "right", borderRight: "2px solid #AAD5FF", fontWeight: 400 }}>
                Sub Total
              </td>
              <td style={{ padding: "4px 10px", textAlign: "right", fontWeight: 400 }}>
                {subTotal.toFixed(2)}
              </td>
            </tr>

            {/* Deposit row (non-Canada) */}
            {!isCanada && (
              <tr style={{ fontSize:15, backgroundColor:"azure" }}>
                <td style={{ padding: "4px 10px", textAlign: "right", borderRight: "2px solid #AAD5FF", color: "#444" }}>
                  Deposit
                </td>
                <td style={{ padding: "4px 10px", textAlign: "right" }}>
                  <input style={{ ...inputStyle, width: 150, textAlign: "right", fontSize: 14 }}
                    value={deposit} onKeyDown={onlyDecimal}
                    onChange={(e) => setDeposit(e.target.value)}
                    onBlur={recalculate} maxLength={12} />
                </td>
              </tr>
            )}

            {/* Discount row (non-Canada) */}
            {!isCanada && (
              <tr style={{ fontSize:15, backgroundColor:"azure"}}>
                <td style={{ padding: "4px 10px", textAlign: "right", borderRight: "2px solid #AAD5FF", color: "#444" }}>
                  Discount
                </td>
                <td style={{ padding: "4px 10px", textAlign: "right" }}>
                  <input style={{ ...inputStyle, width: 150, textAlign: "right", fontSize: 14 }}
                    value={discount} onKeyDown={onlyDecimal}
                    onChange={(e) => setDiscount(e.target.value)}
                    onBlur={recalculate} maxLength={12} />
                </td>
              </tr>
            )}

            {/* GST row */}
            <tr style={{ fontSize:15, backgroundColor:"azure" }}>
              <td style={{ padding: "4px 10px", textAlign: "right", borderRight: "2px solid #AAD5FF", color: "#444" }}>
                GST
              </td>
              <td style={{ padding: "4px 10px", textAlign: "right" }}>{gstAmt.toFixed(2)}</td>
            </tr>

            {/* PST row */}
            <tr style={{ fontSize:15, backgroundColor:"azure"}}>
              <td style={{ padding: "4px 10px", textAlign: "right", borderRight: "2px solid #AAD5FF", color: "#444" }}>
                PST
              </td>
              <td style={{ padding: "4px 10px", textAlign: "right" }}>{pstAmt.toFixed(2)}</td>
            </tr>

            {/* HST row */}
            <tr style={{ fontSize:15, backgroundColor:"azure" }}>
              <td style={{ padding: "4px 10px", textAlign: "right", borderRight: "2px solid #AAD5FF", color: "#444" }}>
                HST
              </td>
              <td style={{ padding: "4px 10px", textAlign: "right" }}>{hstAmt.toFixed(2)}</td>
            </tr>

            {/* Total inc tax */}
            <tr style={{ fontSize:15, backgroundColor:"azure" }}>
              <td style={{ padding: "4px 10px", textAlign: "right", borderRight: "2px solid #AAD5FF", fontWeight: 400 }}>
                Total (inc taxes)
              </td>
              <td style={{ padding: "4px 10px", textAlign: "right", fontWeight: 400 }}>
                {totalIncTax.toFixed(2)}
              </td>
            </tr>

            {/* Deposit inc taxes */}
            <tr style={{ fontSize:15, backgroundColor:"azure" }}>
              <td style={{ padding: "4px 10px", textAlign: "right", borderRight: "2px solid #AAD5FF", color: "#444" }}>
                Deposit (inc taxes)
              </td>
              <td style={{ padding: "4px 10px", textAlign: "right" }}>
                <input
                  style={{
                    ...inputStyle, width: 150, textAlign: "right",
                    border: "1px solid #ff9999", background: "#fff", fontSize: 14,
                  }}
                  value={depositIncTax} onKeyDown={onlyDecimal}
                  onChange={(e) => setDepositIncTax(e.target.value)}
                  onBlur={recalculate} maxLength={12}
                />
              </td>
            </tr>

            {/* Balance */}
            <tr style={{ backgroundColor:"azure", marginBottom : 8, fontSize:16 }}>
              <td style={{
                padding: "6px 10px", textAlign: "right",
                borderRight: "2px solid #AAD5FF",
                fontWeight: 400, fontSize: 16, 
              }}>
                Balance
              </td>
              <td style={{
                padding: "6px 10px", textAlign: "right",
                fontWeight: 700, fontSize: 18,
                borderTop: "2px solid #020202",
                borderBottom: "2px solid #020202",
                color: balance > 0 ? "#000" : "#cc0000",
              }}>
                {balance.toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>

        {/* ── Monitoring ── */}
        <table style={{
          width: "100%", borderCollapse: "collapse",
          border: "2px solid #AAD5FF", background: "#fff", marginTop: 20
        }}>
        <tbody style={{border:"2px solid #AAD5FF", background: "azure"}}>
            <tr style={{ background: "azure", borderBottom: "2px solid #AAD5FF" }}>
              <td style={{ padding: "6px 10px" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                  <input type="checkbox" checked={monitoringEnabled}
                    onChange={(e) => handleMonitoringToggle(e.target.checked)} />
                  <span style={{ fontWeight: 400, fontSize: 15 }}>Monitoring</span>
                </label>
              </td>
            </tr>

            {monitoringEnabled && (
              <tr style={{ borderBottom: "1px solid #ccd9e8" }}>
                <td style={{ padding: "6px 20px", display: "flex", gap: 20 }}>
                  {init.show_hardlocks && (
                    <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                      <input type="radio" name="monitoring"
                        checked={monitoringMode === "mail"}
                        onChange={() => setMonitoringMode("mail")} />
                      Mail Reporting
                    </label>
                  )}
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    <input type="radio" name="monitoring"
                      checked={monitoringMode === "online"}
                      onChange={() => { setMonitoringMode("online"); setEmail1(init.email || ""); }} />
                    Online Reporting
                  </label>
                </td>
              </tr>
            )}

            {monitoringEnabled && monitoringMode === "mail" && (
              <tr style={{ borderBottom: "1px solid #AAD5FF" }}>
                <td style={{ padding: "6px 20px" }}>
                  <label style={labelStyle}>
                    Address
                    <textarea value={mailAddress} onChange={(e) => setMailAddress(e.target.value)}
                      rows={3} style={{ ...inputStyle, resize: "vertical", marginTop: 2 }} />
                  </label>
                </td>
              </tr>
            )}

            {monitoringEnabled && monitoringMode === "online" && (
              <tr style={{ borderBottom: "1px solid #ccd9e8" }}>
                <td style={{ padding: "6px 20px" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {[
                      [email1, setEmail1, "Email 1"],
                      [email2, setEmail2, "Email 2"],
                      [email3, setEmail3, "Email 3"],
                    ].map(([val, setter, lbl]) => (
                      <label key={lbl as string} style={labelStyle}>
                        {lbl as string}
                        <input style={{ ...inputStyle, width: 260 }} type="email"
                          value={val as string}
                          onChange={(e) => (setter as (v: string) => void)(e.target.value)}
                          onBlur={() => {
                            if (val && !validateEmail(val as string))
                              showToast(`Invalid Email ID — ${lbl}`, "error");
                          }} />
                      </label>
                    ))}
                    <p style={{ fontSize: 10, color: "#666", fontStyle: "italic" }}>
                      Note: Please leave blank if you are not sure about the Email IDs
                    </p>
                  </div>
                </td>
              </tr>
            )}

            {/* Support Fee + Free Support */}
            <tr>
              <td style={{ padding: "6px 10px", backgroundColor:"azure", border:"2px solid #AAD5FF" }}>
                <table style={{ borderCollapse: "collapse", width: "100%", backgroundColor:"azure" }}>
                  <tbody style={{backgroundColor:"azure" }}>
                    <tr >
                      <td style={{ padding: "2px 6px", whiteSpace: "nowrap" }}>
                        <span style={{ marginRight: 6, fontSize: 16 }}>Support Fee</span>
                        <input style={{ ...inputStyle, width: 90, display: "inline-block",  fontSize: 14 }}
                          value={supportFee} onKeyDown={onlyDecimal}
                          onChange={(e) => setSupportFee(e.target.value)} maxLength={12} />
                      </td>
                      <td style={{ padding: "2px 6px", whiteSpace: "nowrap" }}>
                        <span style={{ marginRight: 6, fontSize: 16 }}>No.of months Free Support</span>
                        <input style={{ ...inputStyle, width: 70, display: "inline-block",  fontSize: 14 }}
                          value={freeSupport} onKeyDown={onlyNumbers}
                          onChange={(e) => setFreeSupport(e.target.value)} maxLength={3} />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          </tbody>
        </table>

        {/* ── Deposit payment option ── */}
        {showDepositSection && (
          <table style={{
            width: "100%", borderCollapse: "collapse",
            border: "1px solid #7f9db9", borderTop: "none", background: "#fff",
          }}>
            <thead>
              <tr style={{ background: "#aad5ff" }}>
                <th style={{
                  padding: "6px 10px", textAlign: "left", fontWeight: 700,
                  fontSize: 15, borderBottom: "1px solid #7f9db9",
                }}>
                  Deposit Payment / Enrollment Fee - Option
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: "8px 20px" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {(["Cash", "Cheque", "Credit Card", "Direct Debit"] as const).map((m) => (
                      <label key={m} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                        <input type="radio" name="depositMethod"
                          checked={depositMethod === m}
                          onChange={() => setDepositMethod(m)} />
                        {m}
                      </label>
                    ))}
                    <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", marginTop: 4 }}>
                      <input type="checkbox" checked={officeReceived}
                        onChange={(e) => setOfficeReceived(e.target.checked)} />
                      Office Received
                    </label>
                  </div>

                  {depositMethod === "Direct Debit" && (
                    <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                      {[
                        ["BSB #", depositBSB, setDepositBSB],
                        ["A/c #", depositAc, setDepositAc],
                        ["A/c Name", depositAcName, setDepositAcName],
                        ["Bank Name", depositBankName, setDepositBankName],
                        ["Collection Date (dd/MM/yyyy)", collectionDate, setCollectionDate],
                      ].map(([lbl, val, setter]) => (
                        <label key={lbl as string} style={labelStyle}>
                          {lbl as string} <span style={{ color: "#dc2626" }}>*</span>
                          <input style={{ ...inputStyle, width: 200 }}
                            value={val as string}
                            onChange={(e) => (setter as (v: string) => void)(e.target.value)} />
                        </label>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        )}

        {/* ── Balance Payment Option ── */}
        <table style={{
          width: "100%", borderCollapse: "collapse",
          border: "1px solid #7f9db9", borderTop: "none", background: "#fff", marginTop: 12,
        }}>
          <thead>
            <tr style={{ background: "#aad5ff" }}>
              <th style={{
                padding: "6px 10px", textAlign: "left", fontWeight: 700,
                fontSize: 15, borderBottom: "4px solid azure",
              }}>
                Balance Payment Option
                <span style={{ marginLeft: 30, color: "#131313", fontWeight: 400, fontSize: 13 }}>
                  <span style={{ color: "#dc2626" }}>*</span> Contact for payment is same as the primary contact for the progam ?
                </span>
                <span style={{ marginLeft: 12 }}>
                  <label style={{ fontWeight: 400, cursor: "pointer", marginRight: 10 }}>
                    <input type="radio" name="payContact" checked={paymentContactSame}
                      onChange={() => setPaymentContactSame(true)}
                      style={{ marginRight: 4 }} />
                    YES
                  </label>
                  <label style={{ fontWeight: 400, cursor: "pointer" }}>
                    <input type="radio" name="payContact" checked={!paymentContactSame}
                      onChange={() => setPaymentContactSame(false)}
                      style={{ marginRight: 4 }} />
                    NO
                  </label>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Regular installments */}
            <tr>
              <td style={{ padding: 0 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#aad5ff" }}>
                      <th colSpan={5} style={{
                        padding: "8px 10px", textAlign: "left",
                        fontWeight: 700, fontSize: 16,
                        borderBottom: "1px solid #ccd9e8",
                        borderTop: "1px solid #ccd9e8",
                      }}>
                        Payment through regular installments
                      </th>
                    </tr>
                    <tr style={{ background: "#aad5ff" }}>
                      <th style={{ ...th, textAlign: "center", fontSize: 16, width: "18%" }}>Frequency</th>
                      <th style={{ ...th, textAlign: "center", fontSize: 16, width: "22%" }}>Start Date</th>
                      <th style={{ ...th, textAlign: "center", fontSize: 16, width: "18%" }}>Amount</th>
                      <th style={{ ...th, textAlign: "center", fontSize: 16, width: "22%" }}>No. of Installments</th>
                      <th style={{ ...th, textAlign: "center", fontSize: 16, width: "20%" }}>Payment mode</th>
                    </tr>
                  </thead>
                  <tbody style={{ backgroundColor: "azure" }}>
                    <tr style={{ borderBottom: "1px solid #ccd9e8" }}>
                      <td style={{ padding: "5px 8px", borderRight: "2px solid #aad5ff" }}>
                        <select style={{ ...inputStyle, fontSize:"15px"}} value={installFreq}
                          onChange={(e) => setInstallFreq(parseInt(e.target.value))}>
                          <option value={0} style={{fontSize:"15px"}}>--None--</option>
                          <option value={1} style={{fontSize:"15px"}}>Weekly</option>
                          <option value={2} style={{fontSize:"15px"}}>Fortnightly</option>
                          <option value={3} style={{fontSize:"15px"}}>Monthly</option>
                        </select>
                      </td>
                      <td style={{ padding: "5px 8px", borderRight: "2px solid #aad5ff" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <input style={{ ...inputStyle, flex: 1, fontSize:"15px" }} type="text"
                            value={installStartDate}
                            onChange={(e) => setInstallStartDate(e.target.value)}
                            placeholder="dd/MM/yyyy" />
                          <button style={{
                            border: "1px solid #7f9db9", background: "#aad5ff",
                            padding: "3px 6px", cursor: "pointer", fontSize: 10,
                          }}>▼</button>
                        </div>
                      </td>
                      <td style={{ padding: "5px 8px", borderRight: "2px solid #aad5ff" }}>
                        <input style={{ ...inputStyle, fontSize:"15px" }} value={installAmount}
                          onKeyDown={onlyDecimal}
                          onChange={(e) => setInstallAmount(e.target.value)}
                          maxLength={12} />
                      </td>
                      <td style={{ padding: "5px 8px", borderRight: "2px solid #aad5ff" }}>
                        <input style={{ ...inputStyle, fontSize:"15px" }} value={installCount}
                          onKeyDown={onlyNumbers}
                          onChange={(e) => setInstallCount(e.target.value)}
                          maxLength={3} />
                      </td>
                      <td style={{ padding: "5px 8px" }}>
                        <select style={{ ...inputStyle, fontSize:"15px" }} value={paymentMode}
                          onChange={(e) => setPaymentMode(parseInt(e.target.value))}>
                          <option value={0}>--None--</option>
                          <option value={1}>Cash</option>
                          <option value={2}>Cheque</option>
                          <option value={3}>Credit Card</option>
                          <option value={4}>Direct Debit</option>
                        </select>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>

            {/* Credit card section */}
            {paymentMode === 3 && (
              <tr style={{ borderBottom: "1px solid #ccd9e8" }}>
                <td style={{ padding: "6px 20px" }}>
                  <label style={labelStyle}>
                    Credit Card Type <span style={{ color: "#dc2626" }}>*</span>
                    <select style={{ ...inputStyle, width: 140 }} value={creditCardType}
                      onChange={(e) => setCreditCardType(e.target.value)}>
                      <option value="0">Select</option>
                      <option value="1">Diners</option>
                      <option value="2">VISA</option>
                      <option value="3">MC</option>
                      <option value="4">Amex</option>
                    </select>
                  </label>
                </td>
              </tr>
            )}

            {/* Balance Direct Debit */}
            {showDD_balance && (
              <tr style={{ borderBottom: "1px solid #ccd9e8" }}>
                <td style={{ padding: "6px 20px" }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>Balance Direct Debit Details</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {[
                      ["BSB #", balanceBSB, setBalanceBSB],
                      ["A/c #", balanceAcNo, setBalanceAcNo],
                      ["A/c Name", balanceAcName, setBalanceAcName],
                      ["Bank Name", balanceBankName, setBalanceBankName],
                    ].map(([lbl, val, setter]) => (
                      <label key={lbl as string} style={labelStyle}>
                        {lbl as string} <span style={{ color: "#dc2626" }}>*</span>
                        <input style={{ ...inputStyle, width: 200 }}
                          value={val as string}
                          onChange={(e) => (setter as (v: string) => void)(e.target.value)} />
                      </label>
                    ))}
                  </div>
                </td>
              </tr>
            )}

            {/* Custom payments */}
            <tr style={{ borderBottom: "1px solid #ccd9e8" }}>
              <td style={{ padding: "6px 10px", backgroundColor:"#aad5ff"}}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6}}>
                  <span style={{ fontWeight: 700, fontSize:"17px", fontFamily:"Times New Roman" }}>Custom Payments</span>
                  <button onClick={() => {
                    if (balance <= 0) { alert("Balance is zero."); return; }
                    setShowCPModal(true);
                  }} style={{ ...btnVb }}>Add</button>
                </div>
                {customPayments.length > 0 && (
                  <table style={{ borderCollapse: "collapse", fontSize: 15, width: "100%" }}>
                    <thead>
                      <tr style={{ background: "#aad5ff" }}>
                        <th style={{ ...th, textAlign: "right" }}>Amount</th>
                        <th style={{ ...th, textAlign: "center" }}>Payment Date</th>
                        <th style={{ ...th, textAlign: "left" }}>Payment Mode</th>
                        <th style={th}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {customPayments.map((cp) => (
                        <tr key={cp.id} style={{ borderBottom: "1px solid #ccd9e8" }}>
                          <td style={{ ...td, textAlign: "right" }}>{parseFloat(cp.amount).toFixed(2)}</td>
                          <td style={{ ...td, textAlign: "center" }}>{cp.date || "Installation Date"}</td>
                          <td style={td}>{modeLabel(cp.mode)}</td>
                          <td style={{ ...td, textAlign: "center" }}>
                            <button onClick={() => removeCustomPayment(cp.id)}
                              style={{ background: "none", border: "none", color: "#0000cc", cursor: "pointer", fontSize: 15, textDecoration: "underline" }}>
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </td>
            </tr>

            {/* Remaining balance + finance option */}
            <tr>
              <td style={{ padding: "6px 10px", textAlign: "right", backgroundColor:"AZURE" }}>
                <span style={{ marginRight: 16, fontSize: 16 }}>
                  Remaining Balance:&nbsp;
                  <strong style={{ color: remainingBalance > 0 ? "#000" : "#cc0000" }}>
                    {remainingBalance.toFixed(2)}
                  </strong>
                </span>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 16 }}>
                  <input type="checkbox" checked={financeOption}
                    onChange={(e) => setFinanceOption(e.target.checked)} />
                  Pay through Finance
                </label>
              </td>
            </tr>
          </tbody>
        </table>

        {/* ── Order Comments ── */}
        <table style={{
          width: "100%", borderCollapse: "collapse",
          border: "2px solid #AAD5FF", borderTop: "none",
        }}>
          <thead>
            <tr style={{ background: "#aad5ff" }}>
              <th style={{
                padding: "6px 10px", textAlign: "left", fontWeight: 400, fontFamily: "Times New Roman",
                fontSize: 16, borderBottom: "2px solid #AAD5FF",
              }}>Order Comments</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: "1px 1px" }}>
                <textarea value={orderComments} onChange={(e) => setOrderComments(e.target.value)}
                  rows={5} style={{ ...inputStyle, width: "100%", resize: "vertical" }} />
              </td>
            </tr>
          </tbody>
        </table>

        {/* ── Action buttons ── */}
        <table style={{
          width: "100%", borderCollapse: "collapse",
          border: "2px solid #AAD5FF", borderTop: "none",
          background: "#fdd7ea", marginTop: 8,
        }}>
          <tbody style={{ border: "2px solid #AAD5FF"}}>
            <tr>
              <td style={{ padding: "10px", textAlign: "center" }}>
                <button onClick={() => window.history.back()} style={{ ...btnVb, marginRight: 8, minWidth: 60 }}>Back</button>
                <button onClick={handleSubmit} disabled={loading} style={{ ...btnVb, minWidth: 60, opacity: loading ? 0.6 : 1 }}>
                  {loading ? "Submitting…" : "Submit"}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ── Modals ── */}
      {showPackageModal && (
        <PackageModal
          packages={packages}
          selected={selectedPackages}
          onSelect={setSelectedPackages}
          onClose={() => setShowPackageModal(false)}
        />
      )}
      {showCPModal && (
        <CustomPaymentModal
          onAdd={addCustomPayment}
          onClose={() => setShowCPModal(false)}
        />
      )}

      {toast && (
        <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />
      )}
    </>
  );
}

const inputStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  padding: "2px 4px",
  border: "1px solid #7f9db9",
  borderRadius: 0,
  fontSize: 15,
  // fontFamily: "Tahoma, Arial, sans-serif",
  // background: "#fff",
  color: "#000",
  // marginTop: 2,
  // height: 22,
};

const labelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  fontSize: 15,
  fontWeight: 400,
  color: "#000",
  gap: 1,
};

const btnPrimary: React.CSSProperties = {
  background: "#aad5ff",
  color: "#000",
  border: "1px solid #7f9db9",
  borderRadius: 2,
  padding: "3px 12px",
  // fontFamily: "Tahoma, Arial, sans-serif",
  fontWeight: 400,
  fontSize: 15,
  cursor: "pointer",
  height: 24,
};

const btnSecondary: React.CSSProperties = {
  background: "#f0f0f0",
  color: "#000",
  border: "1px solid #7f9db9",
  borderRadius: 2,
  padding: "3px 12px",
  // fontFamily: "Tahoma, Arial, sans-serif",
  fontWeight: 400,
  fontSize: 15,
  cursor: "pointer",
  height: 24,
};

const btnVb: React.CSSProperties = {
  background: "#efefef",
  color: "#000",
  border: "1px solid #74777a",
  borderRadius: 2,
  padding: "2px 30px",
  // fontFamily: "Tahoma, Arial, sans-serif",
  fontWeight: 400,
  fontSize: 14,
  cursor: "pointer",
  // height: 22,
};

const th: React.CSSProperties = {
  padding: "4px 8px",
  textAlign: "center",
  fontWeight: 400,
  fontSize: 15,
  // fontFamily: "Tahoma, Arial, sans-serif",
  borderBottom: "2px solid #AAD5FF",
  borderRight: "2px solid #AAD5FF",
  color: "#000",
};

const td: React.CSSProperties = {
  padding: "4px 8px",
  fontSize: 15,
  // fontFamily: "Tahoma, Arial, sans-serif",
  borderRight: "2px solid #AAD5FF",
};